import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { programData } from '../data/programData';
import type { Activity } from '../types';
import {
  Palette, Map, Utensils, Trees, Gift, Coffee,
  Clock, MapPin, AlertCircle, HelpCircle, Users
} from 'lucide-react';

const Program: React.FC = () => {
  const [activeDay] = useState(0); // Always day 0 

  // Helper to resolve icon components from string names
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Palette': return <Palette />;
      case 'Map': return <Map />;
      case 'Utensils': return <Utensils />;
      case 'Trees': return <Trees />;
      case 'Gift': return <Gift />;
      case 'Coffee': return <Coffee />;
      default: return <HelpCircle />;
    }
  };

  // Group activities by time slot
  const groupActivitiesByTime = (activities: Activity[]) => {
    const grouped: { [key: string]: Activity[] } = {};
    activities.forEach(activity => {
      if (!grouped[activity.time]) {
        grouped[activity.time] = [];
      }
      grouped[activity.time].push(activity);
    });
    return grouped;
  };

  // Timeline item for Day Camp - single activity
  const TimelineActivity: React.FC<{ activity: Activity; isLast: boolean; isFirst: boolean; hasMultiple: boolean }> = ({
    activity,
    isLast,
    isFirst,
    hasMultiple
  }) => (
    <div className="flex items-start space-x-4 group">
      {/* Timeline line and time bubble */}
      <div className="flex flex-col items-center">
        <div className={`text-white px-3 py-2 rounded-full font-bold text-sm shadow-lg ${activity.type === 'open-event'
          ? 'bg-gradient-to-br from-accent-500 to-blue-500'
          : 'bg-gradient-to-br from-primary-500 to-secondary-500'
          }`}>
          {activity.time}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-16 mt-2 ${activity.type === 'open-event'
            ? 'bg-gradient-to-b from-accent-300 to-blue-300'
            : 'bg-gradient-to-b from-primary-300 to-secondary-300'
            }`}></div>
        )}
      </div>

      {/* Activity content */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className={`flex-1 bg-white/10 backdrop-blur-md rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02] ${hasMultiple ? 'mb-4' : 'mb-8'
          } ${activity.type === 'open-event'
            ? 'border border-accent-500/30 ring-2 ring-accent-500/20'
            : 'border border-white/10'
          }`}
      >
        <div className="flex items-start space-x-4">
          <div className="text-3xl flex-shrink-0 text-accent-300">{getIcon(activity.icon)}</div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-bold text-xl text-white">{activity.title}</h4>
              {activity.type === 'open-event' && (
                <span className="bg-gradient-to-r from-accent-500 to-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium ml-3 flex-shrink-0">
                  Atvērts visiem
                </span>
              )}
            </div>
            <p className="text-gray-300 mb-4 leading-relaxed">{activity.description}</p>
            <div className="flex flex-wrap gap-4 text-sm">
              {activity.location && (
                <div className="flex items-center text-gray-300 bg-white/10 px-3 py-1 rounded-full">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{activity.location}</span>
                </div>
              )}
              {activity.ageGroup && (
                <div className="flex items-center text-gray-300 bg-white/10 px-3 py-1 rounded-full">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{activity.ageGroup}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );

  // Multiple activities at same time - compact horizontal layout
  const MultipleActivitiesGroup: React.FC<{ time: string; activities: Activity[]; isLast: boolean }> = ({
    time,
    activities,
    isLast
  }) => (
    <div className="flex items-start space-x-4 group">
      {/* Timeline line and time bubble */}
      <div className="flex flex-col items-center">
        <div className={`text-white px-3 py-2 rounded-full font-bold text-sm shadow-lg ${activities.some(a => a.type === 'open-event')
          ? 'bg-gradient-to-br from-accent-500 to-blue-500'
          : 'bg-gradient-to-br from-primary-500 to-secondary-500'
          }`}>
          {time}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-16 mt-2 ${activities.some(a => a.type === 'open-event')
            ? 'bg-gradient-to-b from-accent-300 to-blue-300'
            : 'bg-gradient-to-b from-primary-300 to-secondary-300'
            }`}></div>
        )}
      </div>

      {/* Multiple activities content */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="flex-1 bg-white/10 backdrop-blur-md rounded-xl shadow-lg border border-white/10 p-6 hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02] mb-8"
      >
        <div className="mb-4">
          <h4 className="font-bold text-lg text-white mb-2">Vairākas aktivitātes</h4>
          <p className="text-gray-300 text-sm">Izvēlieties sev piemērotāko aktivitāti</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {activities.map((activity, actIndex) => (
            <div key={actIndex} className={`p-4 rounded-lg hover:shadow-md transition-all duration-300 ${activity.type === 'open-event'
              ? 'bg-accent-900/30 border border-accent-500/30'
              : 'bg-primary-900/20 border border-primary-500/30'
              }`}>
              <div className="flex items-start space-x-3">
                <div className="text-2xl flex-shrink-0 text-accent-300">{getIcon(activity.icon)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h5 className="font-bold text-white">{activity.title}</h5>
                    {activity.type === 'open-event' && (
                      <span className="bg-gradient-to-r from-accent-500 to-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-medium ml-2 flex-shrink-0">
                        Atvērts
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-300 mb-3 line-clamp-2">{activity.description}</p>
                  <div className="space-y-2">
                    {activity.location && (
                      <div className="flex items-center text-xs text-gray-400">
                        <MapPin className="w-3 h-3 mr-1" />
                        <span className="truncate">{activity.location}</span>
                      </div>
                    )}
                    {activity.ageGroup && (
                      <div className="flex items-center text-xs text-gray-400">
                        <Users className="w-3 h-3 mr-1" />
                        <span>{activity.ageGroup}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <section id="program" className="py-20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-primary-100 to-accent-200 mb-6">
            Pasākuma Programma
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Pilna dienas programma ar dažādām radošajām darbnīcām un aktivitātēm. Piedzīvojumi visai dienai!
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Day Header - Simplified since we have only 1 day */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-block px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full shadow-lg">
              <h3 className="text-2xl font-bold text-white">
                {programData[activeDay].day}, {programData[activeDay].date}
              </h3>
            </div>
          </motion.div>

          {/* Content based on view mode */}
          {/* Day Camp View - Show activities grouped by time for selected day */}
          <motion.div
            key={`day-camp-${activeDay}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4 }}
          >
            {/* Timeline layout */}
            <div className="max-w-4xl mx-auto">
              {(() => {
                const grouped = groupActivitiesByTime(programData[activeDay].activities);
                const timeSlots = Object.entries(grouped);
                let timelineItems: JSX.Element[] = [];

                timeSlots.forEach(([time, activities], index) => {
                  const isLast = index === timeSlots.length - 1;
                  const isFirst = index === 0;

                  if (activities.length === 1) {
                    // Single activity - use timeline format
                    timelineItems.push(
                      <TimelineActivity
                        key={time}
                        activity={activities[0]}
                        isLast={isLast}
                        isFirst={isFirst}
                        hasMultiple={false}
                      />
                    );
                  } else {
                    // Multiple activities - use group format
                    timelineItems.push(
                      <MultipleActivitiesGroup
                        key={time}
                        time={time}
                        activities={activities}
                        isLast={isLast}
                      />
                    );
                  }
                });

                return timelineItems;
              })()}
            </div>
          </motion.div>
        </div>

        {/* Additional Info - Update timing information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 max-w-4xl mx-auto border border-white/10"
        >
          <h3 className="text-2xl font-bold text-center text-white mb-6">
            Svarīga informācija
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold text-primary-400 mb-3 flex items-center">
                <span className="mr-2"><Clock className="w-5 h-5" /></span>
                Laika grafiks
              </h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Sākums: 11:00</li>
                <li>• Uzkodas: 12:00</li>
                <li>• Vakariņas: 15:30</li>
                <li>• Noslēgums: 19:00</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-accent-400 mb-3 flex items-center">
                <span className="mr-2"><MapPin className="w-5 h-5" /></span>
                Lokācija
              </h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• "Dadzis", Viļāni</li>

              </ul>
            </div>
            <div>
              <h4 className="font-bold text-primary-400 mb-3 flex items-center">
                <span className="mr-2"><AlertCircle className="w-5 h-5" /></span>
                Ko ņemt līdzi
              </h4>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li>• Siltu, ērtu apģērbu</li>
                <li>• Piemērotus apavus staigāšanai</li>
                <li>• Pozitīvu noskaņojumu!</li>
                <li>• Bērniem zem 7 gadiem - vecāku pavadība</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Program;
